import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import { Result } from '../Result';
import type { IMethod } from '../../../stores/model/metamodel/method';

/**
 * Check if a method is successfully propagated.
 */
class MethodPropagationCheck extends Check<IMethod> {
  static description = 'Check method propagation';

  check(): Report {
    this.report = new Report();

    // Guard orphan methods: when the owning object type reference is dead
    // (`modelReference` resolves a deleted id to `undefined`), every
    // `this.element.ownerObjectType.*` read below throws and the whole model
    // check rejects (wedging the "Please wait" modal). A method with no owner
    // cannot be propagated, so there is nothing to report. Same dangling-
    // reference guard family as UIModel.findFSMById and the OET orphan guards;
    // valid models always have an ownerObjectType, so check output is unchanged.
    if (this.element.ownerObjectType == null) {
      return this.report;
    }

    const inherited = this.element.provenance === 'INHERITED';
    const specialized = inherited && this.element.specialised;

    // Propagate all methods except specialized inherited methods over the own dependencies
    if (!specialized) {
      for (const dep of this.element.ownerObjectType.dependenciesAsDependent) {
        if (this.element.findAcquiredMethod(dep) === null && !this.element.isLooping()) {
          this.report.addMessage(
            MessageType.WARNING,
            `Method ${this.element.asString()} in object ${this.element.ownerObjectType.asString()} isn't propagated to object ${dep.master.asString()} over dependency ${dep.asString()}`,
            this,
            this.element
          );
        }
      }
    }

    // Propagate all methods except inherited methods over the dependencies of the super types.
    if (!inherited) {
      for (const dep of this.element.ownerObjectType.indirectSupertypes.flatMap((s) =>
        s.dependenciesAsDependent.slice()
      )) {
        if (this.element.findAcquiredMethod(dep) === null && !this.element.isLooping()) {
          this.report.addMessage(
            MessageType.WARNING,
            `Method ${this.element.asString()} in object ${this.element.ownerObjectType.asString()} isn't propagated to object ${dep.master.asString()} over dependency ${dep.asString()}`,
            this,
            this.element
          );
        }
      }
    }

    // Make inherited methods in direct subtypes for all methods except specialized inherited methods.
    if (!specialized) {
      for (const inh of this.element.ownerObjectType.inheritancesAsSupertype) {
        if (this.element.findInheritedMethod(inh) === null && !this.element.isLooping()) {
          this.report.addMessage(
            MessageType.WARNING,
            `Method ${this.element.asString()} in object ${this.element.ownerObjectType.asString()} isn't inherited to object ${inh.subtype.asString()} over inheritance ${inh}`,
            this,
            this.element
          );
        }
      }
    }

    if (this.report.result === Result.NO_RESULT) {
      this.report.addMessage(
        MessageType.INFORMATION,
        `Method ${this.element.asString()} is successfully propagated`,
        this,
        this.element
      );
    }

    return this.report;
  }
}

export default MethodPropagationCheck;
